<script setup lang="ts">
import type { SubmitEventPromise } from 'vuetify';
import type {
  InitialData as NewTicketDialogFormInitialData,
  ValidData as NewTicketDialogFormValidData,
} from '@/components/dialogForms/NewTicketDialogForm.vue';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { computed, useTemplateRef } from 'vue';

import ReportGenerateDialogAlert from '@/components/alerts/ReportGenerateDialogAlert.vue';
import KBtn from '@/components/btns/KBtn.vue';
import NewTicketDialogForm from '@/components/dialogForms/NewTicketDialogForm.vue';
import ReportGenerateDialogHeader from '@/components/headers/ReportGenerateDialogHeader.vue';
import PageDialogWrapper from '@/components/wrappers/PageDialogWrapper.vue';

export interface Props {
  isConfirmLoading?: boolean;
  jobId?: number;
  jobDate?: string;
  opsEmails?: string[];
  channelsEmails?: openapi.ZammadChannelsEmailsContentOpenapi;
}
export type DefProps = typeof props;
const props = withDefaults(defineProps<Props>(), {
  isConfirmLoading: false,
  jobId: undefined,
  jobDate: undefined,
  opsEmails: () => [],
  channelsEmails: () => [],
});
const emit = defineEmits<{
  cancelBtnClick: [event: Event];
  confirmBtnClick: [
    event: SubmitEventPromise | null | undefined,
    formData: NewTicketDialogFormValidData,
    props: DefProps,
  ];
}>();

function handleCancelClick(event: Event): void {
  // also it should be wrapped with <v-dialog :persistent="isConfirmLoading"
  if (props.isConfirmLoading) return;
  emit('cancelBtnClick', event);
}

function handleSubmit(
  event: SubmitEventPromise | null | undefined,
  formData: NewTicketDialogFormValidData,
): void {
  if (props.isConfirmLoading) return;
  emit('confirmBtnClick', event, formData, props);
}

const mainEl = useTemplateRef('main');

function getSubmitBtn(): HTMLInputElement | null {
  if (!mainEl.value) return null;
  return mainEl.value.querySelector('input[type=submit]');
}

function handleConfirmClick(event: Event): void {
  const submitBtn = getSubmitBtn();
  if (!submitBtn) return;
  submitBtn.click();
}

const initialData = computed<NewTicketDialogFormInitialData>(() => {
  return {
    to: undefined,
    cc: props.opsEmails,
    subject: `Update to job #${props.jobId || ''}`,
    message: undefined,
    attachments: undefined,
  };
});
</script>

<template>
  <PageDialogWrapper
    :class="$style.newTicketDialog"
    data-testid="newTicketDialog"
  >
    <template #header>
      <ReportGenerateDialogHeader @close-click="handleCancelClick">
        New ticket
      </ReportGenerateDialogHeader>
    </template>
    <section ref="main" :class="$style.main">
      <NewTicketDialogForm
        :initial-data="initialData"
        :loading="props.isConfirmLoading"
        @submit="handleSubmit"
      />
      <ReportGenerateDialogAlert
        icon="mdi-alert-circle-outline"
        :class="$style.alert"
      >
        The notification will be sent to user(s) you mentioned in «To» field
      </ReportGenerateDialogAlert>
    </section>
    <template #footer>
      <menu :class="$style.dialogFooterMenuList">
        <li :class="$style.dialogFooterMenuListItem">
          <KBtn
            variant="outlined"
            append-icon="mdi-close"
            :class="$style.btn"
            @click="handleCancelClick"
          >
            Cancel
          </KBtn>
        </li>
        <li :class="$style.dialogFooterMenuListItem">
          <KBtn
            append-icon="mdi-plus"
            :class="$style.btn"
            :loading="props.isConfirmLoading"
            @click="handleConfirmClick"
          >
            Create
          </KBtn>
        </li>
      </menu>
    </template>
  </PageDialogWrapper>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use 'sass:math';
@use '@/styles/colors';
@use '@/styles/variables';

.newTicketDialog {
  width: 420px;
}
  .main {
  }
  .alert {
    margin-top: 15px;
  }
  .dialogFooterMenuList {
    list-style: none;
    margin: 0;
    padding: 0;
    text-align: right;
  }
    .dialogFooterMenuListItem {
      display: inline;
    }
    .dialogFooterMenuListItem + .dialogFooterMenuListItem {
      margin-left: 1rem;
    }
      .btn {
      }
</style>
